<template>
  <e-dialog ref="dialogRef" @open="handleDialogOpen" @after-leave="handleDialogAfterLeave">
    <template #title>
      <div class="caption">{{ isEdit ? `#${editedRestriction?.restriction.number}` : 'Новое ограничение' }}</div>
    </template>

    <period-overlap-conflict-alert v-model="form.hasPeriodOverlapConflict" class="mb-6" />

    <e-form v-model="form.isValid" @submit="handleSubmit">
      <div class="d-flex align-start ga-4">
        <e-date-picker
          v-model="form.period.from"
          label="Период с"
          :min="periodFromMin"
          :max="form.period.to"
          :disabled="fromInThePast"
          :error-messages="periodFromErrorMessage"
        />
        <e-date-picker
          v-model="form.period.to"
          label="Период по"
          :min="dayjs.max(form.period.from, dayjs(new Date()))"
          :max="periodToMax"
          :error-messages="periodToErrorMessage"
        />
      </div>
      <template v-if="form.items.length === 1">
        <e-select
          v-model="form.items[0].roomCategory"
          class="mt-6"
          label="Категория номера"
          :items="roomCategoryItems"
          item-title="name[0].value"
          item-value="cid"
          :disabled="!!props.selectedCells.length"
          @update:model-value="handleRoomCategoryUpdate"
        />
        <e-select
          v-model="form.items[0].placingVariant"
          class="mt-4"
          label="Вариант размещения"
          :items="placingVariantItems"
          item-title="restrictionFactor.placingVariant.name"
          item-value="restrictionFactor.cid"
          :disabled="!!props.selectedCells.length"
        />

        <placing-variants-tooltip
          v-if="activePlacingVariant"
          class="placing-variant-restriction-dialog__tooltip paragraph-xs"
          :placing-variant="activePlacingVariant"
        />
      </template>

      <div class="paragraph-l font-600 mt-6">Настройка ограничения</div>
      <div class="paragraph-s text-secondary mt-1 mb-6">
        <template v-if="form.items.length === 1">Настройки должны отличаться от настроек по умолчанию</template>
        <template v-else>Ограничения с настройками по умолчанию не будут созданы</template>
      </div>

      <template v-if="form.items.length === 1">
        <e-radio-group
          v-model="form.items[0].isOpened"
          :items="isOpenedRadioItems"
          inline
          @update:model-value="handleItemIsOpenedUpdate(0, $event)"
        />
        <e-checkbox
          v-model="form.items[0].isNeedConfirm"
          label="Бронь под запрос"
          class="mt-4"
          :disabled="!form.items[0].isOpened"
        />
      </template>
      <div v-else class="d-grid gr-8">
        <group-operation-block
          v-for="(item, index) in form.items"
          :key="index"
          :error="checkIsItemDefault(index) ? 'Ограничение не будет создано' : ''"
          @remove="handleItemRemove(index)"
        >
          <template #title>
            {{ getRoomCategoryNameById(item.roomCategory) }},
            {{ getPlacingVariantNameById(item.roomCategory, item.placingVariant) }}
            <help-tooltip>
              <placing-variants-tooltip
                :placing-variant="getPlacingVariantTooltipById(item.roomCategory, item.placingVariant)"
              />
            </help-tooltip>
          </template>
          <e-radio-group
            v-model="item.isOpened"
            :items="isOpenedRadioItems"
            inline
            @update:model-value="handleItemIsOpenedUpdate(index, $event)"
          />
          <e-checkbox v-model="item.isNeedConfirm" label="Бронь под запрос" class="mt-4" :disabled="!item.isOpened" />
        </group-operation-block>
      </div>

      <div class="d-flex align-center mt-6">
        <remove-restriction-dialog
          v-if="isEdit"
          :number="editedRestriction?.restriction.number"
          :action="removeRestrictionAction"
          :operation-delete="placingVariantsRestrictions?.operations.remove"
          with-activator
        />
        <e-btn variant="secondary" class="mr-4 ml-auto" @click="handleCancel">Отмена</e-btn>
        <e-btn variant="primary" type="submit" :disabled="isSaveButtonDisabled" :loading="form.isLoading">
          {{ isEdit ? 'Сохранить' : 'Создать' }}
        </e-btn>
      </div>
    </e-form>
  </e-dialog>
</template>

<script setup lang="ts">
import dayjs from 'dayjs';
import { computed, reactive, ref, toRef } from 'vue';
import { useRoute } from 'vue-router';
import type { ChessDateCellType } from '@/core/components/interface/Chess/Chess.type';
import type { AddRestrictionsPlacingVariantsInput, RangeOfDateOnly } from '@/graphql/generated';
import type {
  PlacingVariantsRestrictionsCellDataType,
  PlacingVariantsRestrictionsTableItemType,
} from '@/parts/tariffs/store/restrictions';
import EDialog from '@/core/components/modals/Dialog.vue';
import { EBtn, ECheckbox, EDatePicker, EForm, ERadioGroup, ESelect, HelpTooltip } from '@/core/components/ui';
import { useSnackbar } from '@/core/composables/useSnackbar';
import { getArrayMinMax } from '@/core/utils/array';
import { getDateWithUtcOffset, getIsoDateFormat, getIsoDateWithUtcOffset, isDayBefore } from '@/core/utils/date';
import { errorTypes } from '@/parts/errors/utils/error-types';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';
import PlacingVariantsTooltip from '@/parts/tariffs/components/PlacingVariantsTooltip.vue';

import GroupOperationBlock from '@/parts/tariffs/components/PricesAndRestrictions/GroupOperationBlock.vue';
import PeriodOverlapConflictAlert from '@/parts/tariffs/components/PricesAndRestrictions/PeriodOverlapConflictAlert.vue';
import RemoveRestrictionDialog from '@/parts/tariffs/components/PricesAndRestrictions/RemoveRestrictionDialog.vue';
import { useRestrictionsStore } from '@/parts/tariffs/store/restrictions';

const isOpenedRadioItems = [
  { title: 'Продажа открыта', value: true },
  { title: 'Продажа закрыта', value: false },
];

const props = defineProps<{
  selectedCells: ChessDateCellType<PlacingVariantsRestrictionsCellDataType>[];
}>();

const $route = useRoute();
const { showSnackbar } = useSnackbar();
const { timeZoneOffsetMin } = useAccommodationObjectsStore();
const {
  placingVariantsRestrictions,
  placingVariantsRestrictionsChessPeriod,
  getPlacingVariantsRestrictions,
  addPlacingVariantsRestriction,
  updatePlacingVariantsRestriction,
  removePlacingVariantsRestriction,
} = useRestrictionsStore();

const dialogRef = ref();

const form = reactive<{
  period: RangeOfDateOnly;
  items: Array<{
    roomCategory: string | null;
    placingVariant: string | null;
    isOpened: boolean;
    isNeedConfirm: boolean;
  }>;

  isValid: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  hasPeriodOverlapConflict: boolean;
}>({
  period: { from: '', to: '' },
  items: [],

  isValid: false,
  isLoading: false,
  isSuccess: false,
  hasPeriodOverlapConflict: false,
});

const fromInThePast = computed(() => form.period.from && isDayBefore(form.period.from, new Date()));

const isEdit = ref(false);
const editedRestriction = ref<PlacingVariantsRestrictionsTableItemType>();

const tariffScheduleCid = computed(() => String($route.params.t_id));

const roomCategoryItems = computed(() => {
  return placingVariantsRestrictions.value?.grid.map((item) => item.roomCategory) ?? [];
});

const placingVariantItems = computed(() => {
  return (
    placingVariantsRestrictions.value?.grid.find((item) => item.roomCategory.cid === form.items[0].roomCategory)
      ?.roomPlacings ?? []
  );
});

const activePlacingVariant = toRef(() => {
  if (form.items.length > 1) {
    return undefined;
  }

  if (form.items[0].placingVariant) {
    const placingVariant = placingVariantItems.value?.find(
      (p) => p.restrictionFactor.cid === form.items[0].placingVariant,
    );

    if (placingVariant) {
      return placingVariant.restrictionFactor.placingVariant;
    }
  }

  return undefined;
});

const periodFromMin = computed(() => {
  const todayWithTimeZoneOffset = getIsoDateWithUtcOffset(new Date(), timeZoneOffsetMin.value);
  if (placingVariantsRestrictionsChessPeriod.value?.from > todayWithTimeZoneOffset) {
    return getDateWithUtcOffset(placingVariantsRestrictionsChessPeriod.value?.from, timeZoneOffsetMin.value);
  }
  if (editedRestriction.value && editedRestriction.value.restriction.period.from < todayWithTimeZoneOffset) {
    return getDateWithUtcOffset(editedRestriction.value.restriction.period.from, timeZoneOffsetMin.value);
  }
  return getDateWithUtcOffset(todayWithTimeZoneOffset, timeZoneOffsetMin.value);
});

const periodToMax = computed(() => {
  return getDateWithUtcOffset(placingVariantsRestrictionsChessPeriod.value?.to, timeZoneOffsetMin.value);
});

const isSaveButtonDisabled = computed(() => {
  if (!form.isValid) {
    return true;
  }

  if (!form.items.some((item, index) => item.roomCategory && item.placingVariant && !checkIsItemDefault(index))) {
    return true;
  }

  if (editedRestriction.value) {
    const item = form.items[0];
    const restriction = editedRestriction.value.restriction;

    if (
      item.isOpened === restriction.options.isOpened &&
      item.isNeedConfirm === (restriction.options.options?.isNeedConfirm ?? false) &&
      getIsoDateFormat(form.period.from) === restriction.period.from &&
      getIsoDateFormat(form.period.to) === restriction.period.to
    ) {
      return true;
    }
  }

  return false;
});

const periodFromErrorMessage = computed(() => {
  const periodFrom = dayjs(form.period.from).format('YYYY-MM-DD');
  const periodTo = dayjs(form.period.to).format('YYYY-MM-DD');

  if (
    placingVariantsRestrictions.value?.table.some((item) => {
      return (
        item.restriction.period.to >= periodFrom &&
        item.restriction.period.to <= periodTo &&
        item.restriction.restrictionId !== editedRestriction.value?.restriction.restrictionId &&
        form.items.some(
          (formItem) =>
            formItem.roomCategory === item.restrictionFactor.roomCategory.cid &&
            formItem.placingVariant === item.restrictionFactor.roomPlacing.cid,
        ) &&
        !form.isLoading &&
        !form.isSuccess
      );
    })
  ) {
    return 'Наложение дат с существующим ограничением';
  }

  return '';
});

const periodToErrorMessage = computed(() => {
  const periodFrom = dayjs(form.period.from).format('YYYY-MM-DD');
  const periodTo = dayjs(form.period.to).format('YYYY-MM-DD');

  if (
    placingVariantsRestrictions.value?.table.some((item) => {
      return (
        item.restriction.period.from >= periodFrom &&
        item.restriction.period.from <= periodTo &&
        item.restriction.restrictionId !== editedRestriction.value?.restriction.restrictionId &&
        form.items.some(
          (formItem) =>
            formItem.roomCategory === item.restrictionFactor.roomCategory.cid &&
            formItem.placingVariant === item.restrictionFactor.roomPlacing.cid,
        ) &&
        !form.isLoading &&
        !form.isSuccess
      );
    })
  ) {
    return 'Наложение дат с существующим ограничением';
  }

  return '';
});

const getDefaultOptions = (roomCategory?: string | null, placingVariant?: string | null) => {
  return placingVariantsRestrictions.value?.defaults.find((item) => {
    return (
      item.restrictionFactorKey.roomCategoryCid === roomCategory &&
      item.restrictionFactorKey.roomPlacingCid === placingVariant
    );
  })?.defaultOptions;
};

const getRoomCategoryNameById = (id: string | null) => {
  return placingVariantsRestrictions.value?.grid.find((item) => item.roomCategory.cid === id)?.roomCategory.name[0]
    .value;
};

const getPlacingVariantNameById = (roomCategoryId: string | null, placingVariantId: string | null) => {
  return placingVariantsRestrictions.value?.grid
    .find((item) => item.roomCategory.cid === roomCategoryId)
    ?.roomPlacings?.find((item) => item.restrictionFactor.cid === placingVariantId)?.restrictionFactor.placingVariant
    .name;
};

const getPlacingVariantTooltipById = (roomCategoryId: string | null, placingVariantId: string | null) => {
  return placingVariantsRestrictions.value?.grid
    .find((item) => item.roomCategory.cid === roomCategoryId)
    ?.roomPlacings?.find((item) => item.restrictionFactor.cid === placingVariantId)?.restrictionFactor?.placingVariant;
};

const handleRoomCategoryUpdate = () => {
  form.items.forEach((item) => {
    item.placingVariant = null;
  });
};

const handleDialogOpen = () => {
  if (props.selectedCells.length) {
    const editedRestrictionId = props.selectedCells.find((item) => item.data?.restrictionId)?.data?.restrictionId;

    if (editedRestrictionId) {
      editedRestriction.value = placingVariantsRestrictions.value?.table.find(
        (item) => item.restriction.restrictionId === editedRestrictionId,
      );

      form.period = {
        from: getDateWithUtcOffset(editedRestriction.value?.restriction.period.from, timeZoneOffsetMin.value),
        to: getDateWithUtcOffset(editedRestriction.value?.restriction.period.to, timeZoneOffsetMin.value),
      };
      form.items = [
        {
          roomCategory: editedRestriction.value?.restrictionFactor.roomCategory.cid ?? null,
          placingVariant: editedRestriction.value?.restrictionFactor.roomPlacing.cid ?? null,
          isOpened: editedRestriction.value?.restriction.options.isOpened ?? true,
          isNeedConfirm: editedRestriction.value?.restriction.options.options?.isNeedConfirm ?? false,
        },
      ];
      isEdit.value = true;
    } else {
      const periodFromTo = getArrayMinMax(props.selectedCells.map((item) => item.date));
      form.period = {
        from: getDateWithUtcOffset(periodFromTo[0], timeZoneOffsetMin.value),
        to: getDateWithUtcOffset(periodFromTo[1], timeZoneOffsetMin.value),
      };
      props.selectedCells.forEach((cell) => {
        if (
          !form.items.some((item) => {
            return item.roomCategory === cell.data?.roomCategory && item.placingVariant === cell.data?.placingVariant;
          })
        ) {
          const defaultOptions = getDefaultOptions(cell.data?.roomCategory, cell.data?.placingVariant);

          form.items.push({
            roomCategory: cell.data?.roomCategory ?? null,
            placingVariant: cell.data?.placingVariant ?? null,
            isOpened: defaultOptions?.isOpened ?? true,
            isNeedConfirm: defaultOptions?.options?.isNeedConfirm ?? false,
          });
        }
      });
    }
  } else {
    const defaultOptions = placingVariantsRestrictions.value?.defaults[0].defaultOptions;

    form.period = {
      from: getDateWithUtcOffset(periodFromMin.value, timeZoneOffsetMin.value),
      to: getDateWithUtcOffset(periodToMax.value, timeZoneOffsetMin.value),
    };
    form.items = [
      {
        roomCategory: null,
        placingVariant: null,
        isOpened: defaultOptions?.isOpened ?? true,
        isNeedConfirm: defaultOptions?.options?.isNeedConfirm ?? false,
      },
    ];
  }
};

const handleDialogAfterLeave = () => {
  form.period = { from: '', to: '' };
  form.items = [];
  form.isSuccess = false;
  isEdit.value = false;
  editedRestriction.value = undefined;
  if (form.hasPeriodOverlapConflict) {
    getPlacingVariantsRestrictions(tariffScheduleCid.value);
    form.hasPeriodOverlapConflict = false;
  }
};

const checkIsItemDefault = (index: number) => {
  const defaultOptions = getDefaultOptions(form.items[index].roomCategory, form.items[index].placingVariant);

  const defaultIsOpened = defaultOptions?.isOpened ?? true;
  const defaultIsNeedConfirm = defaultOptions?.options?.isNeedConfirm ?? false;

  if (form.items[index].isOpened !== defaultIsOpened || form.items[index].isNeedConfirm !== defaultIsNeedConfirm) {
    return false;
  }

  return true;
};

const handleItemIsOpenedUpdate = (index: number, value: boolean) => {
  if (!value) {
    form.items[index].isNeedConfirm = false;
  }
};

const handleItemRemove = (index: number) => {
  form.items.splice(index, 1);
};

const handleSubmit = async () => {
  if (!form.isValid) return;

  const period = {
    from: dayjs(form.period.from).format('YYYY-MM-DD'),
    to: dayjs(form.period.to).format('YYYY-MM-DD'),
  };

  form.isLoading = true;

  if (editedRestriction.value) {
    const result = await updatePlacingVariantsRestriction({
      options: {
        isOpened: form.items[0].isOpened,
        options: form.items[0].isOpened ? { isNeedConfirm: form.items[0].isNeedConfirm } : undefined,
      },
      period,
      restrictionId: editedRestriction.value.restriction.restrictionId,
      tariffScheduleCid: tariffScheduleCid.value,
    });

    if (!result?.errors) {
      form.isSuccess = true;
      dialogRef.value.close();

      showSnackbar({ type: 'success', title: 'Ограничение ВР изменено' });
    }
  } else {
    const newRestrictionsData = form.items
      .filter((_, index) => !checkIsItemDefault(index))
      .reduce<Record<string, AddRestrictionsPlacingVariantsInput['placingVariants']>>((result, item) => {
        if (!item.roomCategory) {
          return result;
        }

        if (!result[item.roomCategory]) {
          result[item.roomCategory] = [];
        }

        result[item.roomCategory].push({
          options: {
            isOpened: item.isOpened,
            options: item.isOpened ? { isNeedConfirm: item.isNeedConfirm } : undefined,
          },
          period,
          roomPlacingCid: item.placingVariant ?? '',
        });

        return result;
      }, {});

    const results = [];

    for (const [roomCategoryCID, placingVariants] of Object.entries(newRestrictionsData)) {
      results.push(
        await addPlacingVariantsRestriction({
          placingVariants,
          roomCategoryCID,
          tariffScheduleCid: tariffScheduleCid.value,
        }),
      );
    }

    if (results.every((result) => !result?.errors)) {
      form.isSuccess = true;
      dialogRef.value.close();

      showSnackbar({ type: 'success', title: 'Ограничение ВР создано' });
    } else if (
      results.some((result) => result?.errors?.[0].__typename === errorTypes.RestrictionPeriodOverlapConflictError)
    ) {
      form.hasPeriodOverlapConflict = true;
    }
  }

  form.isLoading = false;
};

const removeRestrictionAction = async () => {
  if (!editedRestriction.value) return;

  await removePlacingVariantsRestriction({
    restrictionId: editedRestriction.value.restriction.restrictionId,
    tariffScheduleCid: tariffScheduleCid.value,
  });
  dialogRef.value.close();
};

const handleCancel = () => {
  dialogRef.value.close();
};
</script>

<style lang="scss" scoped>
.placing-variant-restriction-dialog {
  &__tooltip {
    position: relative;
    z-index: 1;

    flex-direction: row;
    flex-wrap: wrap;

    margin-top: 8px;
    padding: 8px 16px;

    color: $text-secondary-color;
    column-gap: 40px;

    background-color: $primary-light-color;
    border-radius: $input-border-radius;

    &:before {
      content: '';
      position: absolute;
      bottom: 100%;
      left: 16px;
      z-index: -1;

      width: 12px;
      height: 12px;

      background-color: $primary-light-color;

      transform: scaleY(1.6) translateY(60%) rotate(45deg);
    }
  }
}
</style>
